import React from 'react';

import { makeStyles, Theme } from '@material-ui/core';
// eslint-disable-next-line import/named
import { TranslateFunction } from 'react-localize-redux';

import { fdApi } from '../../../core/api';
import { urlUtils } from '../../../utils';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import { Routes } from '../../../weborder/Routes';
import { Button, Typography } from '../../atoms';
import { Dialog } from '../../layouts';

type OuterProps = {
  basketItems: any;
  customer: { status: string };
  menuId: number;
  params: Routes['params'];
  pushLoginUrl: (url: string) => void;
  removeItemFromBasket: (payload: {
    index: number;
    indexName?: string;
    menuId: number;
  }) => void;
  restaurantData?:
    | Api.CollectionRestaurantDetail
    | Api.DeliveryRestaurantDetail;
  translate: TranslateFunction;
  updateCustomer: () => void;
};

type Props = OuterProps;

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .fd-text__h1': {
      marginBottom: theme.spacing(2.5),
    },
    '& .fd-text__body1': {
      color: theme.palette.grey[800],
    },
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(0, 0, 7),
    },
  },
}));

export function Over18Dialog(props: Props): JSX.Element {
  const {
    basketItems,
    customer,
    menuId,
    params,
    pushLoginUrl,
    removeItemFromBasket,
    restaurantData,
    translate,
    updateCustomer,
  } = props;
  const classes = useStyles();

  const loginAndNavigateTo = (): void => {
    const urlHashPath = window.location.hash.substring(1);
    pushLoginUrl(urlHashPath);

    const redirectUrl =
      params.type === 'delivery' ? `/phone/${params.id}` : '/phone';
    urlUtils.navigateToHash(redirectUrl);
  };

  const handleRemoveAlcohol = (): void => {
    const itemsToBeRemoved: number[] = [];
    const itemsToKeep = [];

    basketItems.items.forEach((item, index) => {
      if (item.Item.Alcohol) {
        itemsToBeRemoved.push(index);
      } else {
        itemsToKeep.push(item);
      }
    });

    itemsToBeRemoved
      .reverse() // ICH+2022.03.29 - carried over for unclear connection between basket items and removal process
      .forEach((itemIndex) =>
        removeItemFromBasket({ index: itemIndex, menuId }),
      );

    const currentBasketTotal = BasketControl.Total(itemsToKeep);

    const nextPage =
      currentBasketTotal < restaurantData.MinimumOrder
        ? 'restaurant'
        : 'checkout';

    const { concessionStoreId, id, type, vrid } = params;
    const concessionStoreIdPath = concessionStoreId
      ? `/${concessionStoreId}`
      : '';
    const nextPagePath = `/${nextPage}/${vrid}/${type}/${id}${concessionStoreIdPath}`;

    if (customer.status === 'success') {
      urlUtils.navigateToHash(nextPagePath);
    } else {
      loginAndNavigateTo();
    }
  };

  const handleConfirmOver18 = (): void => {
    fdApi.use(fdApi.user.putOverAlcoholAgeLimit()).then(() => {
      updateCustomer();
    });

    if (customer.status === 'success') {
      const nextUrl = `/checkout/${params.vrid}/${params.type}/${params.id}`;
      urlUtils.navigateToHash(nextUrl);
    } else {
      loginAndNavigateTo();
    }
  };

  const dialogActions = (
    <>
      <Button fullWidth onClick={handleRemoveAlcohol}>
        {translate('Over18Dialog_Button_RemoveAlcohol')}
      </Button>
      <Button fullWidth onClick={handleConfirmOver18}>
        {translate('Over18Dialog_Button_ImOver18')}
      </Button>
    </>
  );

  return (
    <Dialog
      actions={dialogActions}
      className={classes.dialog}
      dialogName="over-18"
      open
    >
      <Typography variant="h1">
        {translate('Over18Dialog_AreYouOver18')}
      </Typography>
      <Typography variant="body1">
        {translate('Over18Dialog_Message_Over18Message')}
      </Typography>
    </Dialog>
  );
}
